// Speech-to-text via the Web Speech API. Continuous mode with interim
// transcripts and 2s silence detection (dispatches a `speechend` window event).
//
// Ported from FlowStateNexus src/hooks/useSpeechRecognition.js (architecture
// §11.3.3) and converted to typed TSX. Browser-native — no backend.

import { useCallback, useEffect, useRef, useState } from 'react';

// Minimal ambient types for the Web Speech API (not in the standard TS DOM lib).
interface SRResult {
  readonly isFinal: boolean;
  readonly length: number;
  item(i: number): { transcript: string };
  [index: number]: { transcript: string };
}
interface SRResultList {
  readonly length: number;
  item(i: number): SRResult;
  [index: number]: SRResult;
}
interface SRRecognitionEvent {
  readonly resultIndex: number;
  readonly results: SRResultList;
}
interface SRErrorEvent {
  readonly error: string;
}
interface SRRecognition {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  onstart: (() => void) | null;
  onresult: ((e: SRRecognitionEvent) => void) | null;
  onerror: ((e: SRErrorEvent) => void) | null;
  onend: (() => void) | null;
}
type SRConstructor = new () => SRRecognition;

function getRecognitionCtor(): SRConstructor | null {
  const w = window as unknown as {
    SpeechRecognition?: SRConstructor;
    webkitSpeechRecognition?: SRConstructor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export interface SpeechRecognitionState {
  isListening: boolean;
  transcript: string;
  interimTranscript: string;
  isSupported: boolean;
  error: string | null;
  startListening: () => void;
  stopListening: () => void;
  resetTranscript: () => void;
}

/** Window event dispatched when the user finishes speaking (2s silence). */
export const SPEECH_END_EVENT = 'speechend';

export function useSpeechRecognition(): SpeechRecognitionState {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [isSupported, setIsSupported] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const recognitionRef = useRef<SRRecognition | null>(null);
  const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const finalTranscriptRef = useRef('');

  useEffect(() => {
    const Ctor = getRecognitionCtor();
    if (!Ctor) {
      setIsSupported(false);
      setError('Speech recognition is not supported in this browser. Please use Chrome, Edge, or Safari.');
      return;
    }

    setIsSupported(true);
    const recognition = new Ctor();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setIsListening(true);
      setError(null);
    };

    recognition.onresult = (event) => {
      let interimText = '';
      let finalText = finalTranscriptRef.current;

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const text = result[0].transcript;

        if (result.isFinal) {
          finalText += text + ' ';
          finalTranscriptRef.current = finalText;
          setTranscript(finalText);

          if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
          silenceTimerRef.current = setTimeout(() => {
            window.dispatchEvent(
              new CustomEvent(SPEECH_END_EVENT, {
                detail: { transcript: finalTranscriptRef.current.trim() },
              }),
            );
          }, 2000);
        } else {
          interimText += text;
        }
      }

      setInterimTranscript(interimText);
    };

    recognition.onerror = (event) => {
      if (event.error === 'no-speech' || event.error === 'aborted') return;
      setError(`Speech recognition error: ${event.error}`);
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
      // Restart if we're still meant to be listening (continuous mode).
      if (recognitionRef.current && recognitionRef.current.continuous) {
        try {
          recognition.start();
        } catch {
          /* already started / not restartable */
        }
      }
    };

    recognitionRef.current = recognition;

    return () => {
      if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
      try {
        recognitionRef.current?.stop();
      } catch {
        /* noop */
      }
      recognitionRef.current = null;
    };
  }, []);

  const startListening = useCallback(() => {
    if (!recognitionRef.current) {
      setError('Speech recognition not initialized');
      return;
    }
    try {
      finalTranscriptRef.current = '';
      setTranscript('');
      setInterimTranscript('');
      setError(null);
      recognitionRef.current.continuous = true; // re-arm (stopListening clears it)
      recognitionRef.current.start();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start listening');
    }
  }, []);

  const stopListening = useCallback(() => {
    if (!recognitionRef.current) return;
    try {
      // Clear continuous so onend doesn't auto-restart.
      recognitionRef.current.continuous = false;
      recognitionRef.current.stop();
      if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    } catch {
      /* noop */
    }
  }, []);

  const resetTranscript = useCallback(() => {
    finalTranscriptRef.current = '';
    setTranscript('');
    setInterimTranscript('');
  }, []);

  return {
    isListening,
    transcript,
    interimTranscript,
    isSupported,
    error,
    startListening,
    stopListening,
    resetTranscript,
  };
}
